import { useEffect } from 'react';
import { useSettings } from '../../context/SettingsContext';

const DEFAULT_IMAGE = '/og-default.jpg';

/**
 * Upsert a <meta> tag in document.head keyed by attr/key.
 * Returns an unsetter that restores the element if we created it.
 */
const setMeta = (attr, key, content) => {
  if (!content) return () => {};
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', String(content));
  return () => {
    if (created) {
      el.remove();
    } else if (prev !== null) {
      el.setAttribute('content', prev);
    }
  };
};

const setLinkRel = (rel, href) => {
  if (!href) return () => {};
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return () => {
    if (created) el.remove();
    else if (prev !== null) el.setAttribute('href', prev);
  };
};

/**
 * SEO — sets <title>, description, Open Graph, Twitter Cards, and canonical for the current route.
 * Implemented with a direct-DOM useEffect to stay compatible with React 19.
 */
const SEO = ({
  title,
  description,
  image,
  type = 'website',
  url,
  keywords,
  publishedTime,
  category,
  noIndex = false,
  fullTitle = false,   // when true, `title` is used verbatim without " | SITE_NAME" suffix
}) => {
  const { settings } = useSettings();
  const SITE_NAME = settings.site_title || "Haber Türkiye'den";
  const DEFAULT_DESCRIPTION = settings.meta_description ||
    "Türkiye'nin önde gelen ekonomi ve iş dünyası haber portalı.";
  const DEFAULT_TITLE = SITE_NAME;

  useEffect(() => {
    const pageTitle = title
      ? (fullTitle ? title : `${title} | ${SITE_NAME}`)
      : DEFAULT_TITLE;
    const rawTitle = title || DEFAULT_TITLE;
    const pageDesc = description || DEFAULT_DESCRIPTION;
    const pageImage = image || DEFAULT_IMAGE;
    const origin = window.location.origin;
    const path = url || window.location.pathname;
    const absUrl = path.startsWith('http') ? path : `${origin}${path}`;
    const absImage = pageImage.startsWith('http') ? pageImage : `${origin}${pageImage}`;

    const prevTitle = document.title;
    document.title = pageTitle;

    const cleanups = [
      setMeta('name', 'description', pageDesc),
      keywords ? setMeta('name', 'keywords', keywords) : () => {},
      noIndex ? setMeta('name', 'robots', 'noindex,nofollow') : () => {},

      setMeta('property', 'og:site_name', SITE_NAME),
      setMeta('property', 'og:title', rawTitle),
      setMeta('property', 'og:description', pageDesc),
      setMeta('property', 'og:type', type),
      setMeta('property', 'og:url', absUrl),
      setMeta('property', 'og:image', absImage),
      setMeta('property', 'og:locale', 'tr_TR'),
      type === 'article' && publishedTime
        ? setMeta('property', 'article:published_time', publishedTime)
        : () => {},
      type === 'article' && category
        ? setMeta('property', 'article:section', category)
        : () => {},

      setMeta('name', 'twitter:card', 'summary_large_image'),
      setMeta('name', 'twitter:title', rawTitle),
      setMeta('name', 'twitter:description', pageDesc),
      setMeta('name', 'twitter:image', absImage),

      setLinkRel('canonical', absUrl),
    ];

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn && fn());
    };
  }, [title, description, image, type, url, keywords, publishedTime, category, noIndex, fullTitle, SITE_NAME, DEFAULT_DESCRIPTION, DEFAULT_TITLE]);

  return null;
};

export default SEO;
